import { createContext } from 'react'
import type {
  DashboardBook,
  DashboardUserProfile,
} from '@services/firebase'
import type {
  LearningLesson,
  LearningWorkbookQuizItem,
} from '@services/firebase/learning'

export interface DashboardLanguage {
  readonly enabled: boolean
  readonly flag: string
  readonly name: Record<string, string>
  readonly value: string
}

export interface DashboardWeekday {
  readonly name: Record<string, string>
  readonly value: string
}

export type DashboardSettingsTab =
  | 'account'
  | 'avatar'
  | 'change-password'
  | 'security'
  | 'systems'
  | 'personal'
  | 'subscription'
  | 'study-plan'

export interface DashboardListeningQuizSession {
  readonly bookId: string | null
  readonly createdAt: number
  readonly lesson: LearningLesson
  readonly onlineQuizItems: LearningWorkbookQuizItem[]
}

export interface DashboardState {
  readonly book: DashboardBook | null
  readonly books: DashboardBook[]
  readonly enterKeyPremium: boolean
  readonly errorMessage: string | null
  readonly inWeek: DashboardWeekday[]
  readonly isBootstrapping: boolean
  readonly isLessonsLoading: boolean
  readonly lesson: LearningLesson | null
  readonly lessons: LearningLesson[]
  readonly lessonsBookId: string | null
  readonly lessonsErrorMessage: string | null
  readonly listeningQuizSession: DashboardListeningQuizSession | null
  readonly languages: DashboardLanguage[]
  readonly myUser: DashboardUserProfile | null
  readonly screen: number
  readonly settingsTab: DashboardSettingsTab | null
  readonly selectPremiumAccountPayment: boolean
  readonly showChangeDailyGoal: boolean
  readonly showLanguage: boolean
}

export interface DashboardStateActions {
  readonly mergeMyUser: (user: Partial<DashboardUserProfile>) => void
  readonly setBook: (book: DashboardBook | null) => void
  readonly setBooks: (books: DashboardBook[]) => void
  readonly setBootstrapping: (isBootstrapping: boolean) => void
  readonly setEnterKeyPremium: (enterKeyPremium: boolean) => void
  readonly setErrorMessage: (errorMessage: string | null) => void
  readonly setLesson: (lesson: LearningLesson | null) => void
  readonly setLessons: (lessons: LearningLesson[]) => void
  readonly setLessonsBookId: (lessonsBookId: string | null) => void
  readonly setLessonsErrorMessage: (lessonsErrorMessage: string | null) => void
  readonly setLessonsLoading: (isLessonsLoading: boolean) => void
  readonly setListeningQuizSession: (
    listeningQuizSession: DashboardListeningQuizSession | null,
  ) => void
  readonly setMyUser: (user: DashboardUserProfile | null) => void
  readonly setScreen: (screen: number) => void
  readonly setSettingsTab: (settingsTab: DashboardSettingsTab | null) => void
  readonly setSelectPremiumAccountPayment: (
    selectPremiumAccountPayment: boolean,
  ) => void
  readonly setShowChangeDailyGoal: (showChangeDailyGoal: boolean) => void
  readonly setShowLanguage: (showLanguage: boolean) => void
}

export interface DashboardStateContextValue {
  readonly actions: DashboardStateActions
  readonly state: DashboardState
}

export function normalizeDashboardLanguageValue(language: string | undefined) {
  if (!language) {
    return 'en'
  }

  if (language === 'vi-VN') {
    return 'vi'
  }

  if (language === 'en-US') {
    return 'en'
  }

  if (language.toLowerCase().split('-')[0] === 'es') {
    return 'es'
  }

  if (language === 'cn' || language === 'zh-CN' || language.startsWith('zh-')) {
    return 'zh'
  }

  return language
}

export function getDashboardLocaleKey(language: string | undefined) {
  return normalizeDashboardLanguageValue(language)
}

export function findDashboardLanguage(language: string | undefined) {
  const normalizedLanguage = normalizeDashboardLanguageValue(language)

  return (
    dashboardLanguages.find((entry) => entry.value === normalizedLanguage) ??
    dashboardLanguages[0]
  )
}

export const dashboardLanguages: DashboardLanguage[] = [
  {
    enabled: true,
    flag: '/flags/VN.svg',
    name: {
      cn: '越南文',
      de: 'Vietnamesisch',
      en: 'Vietnamese',
      es: 'Vietnamita',
      fr: 'Vietnamien',
      ja: 'ベトナム語',
      ko: '베트남어',
      ru: 'вьетнамский',
      vi: 'Tiếng Việt',
      zh: '越南语',
    },
    value: 'vi',
  },
  {
    enabled: true,
    flag: '/flags/GB.svg',
    name: {
      cn: '英语',
      de: 'Englisch',
      en: 'English',
      es: 'Inglés',
      fr: 'Anglais',
      ja: '英語',
      ko: '영어',
      ru: 'английский',
      vi: 'Tiếng Anh',
      zh: '英语',
    },
    value: 'en',
  },
  {
    enabled: true,
    flag: '/flags/KR.svg',
    name: {
      cn: '韩语',
      de: 'Koreanisch',
      en: 'Korean',
      es: 'Coreano',
      fr: 'Coréen',
      ja: '韓国語',
      ko: '한국어',
      ru: 'корейский',
      vi: 'Tiếng Hàn',
      zh: '韩语',
    },
    value: 'ko',
  },
  {
    enabled: true,
    flag: '/flags/JP.svg',
    name: {
      cn: '日语',
      de: 'Japanisch',
      en: 'Japanese',
      es: 'Japonés',
      fr: 'Japonais',
      ja: '日本語',
      ko: '일본어',
      ru: 'японский',
      vi: 'Tiếng Nhật',
      zh: '日语',
    },
    value: 'ja',
  },
  {
    enabled: true,
    flag: '/flags/CN.svg',
    name: {
      cn: '中文',
      de: 'Chinesisch',
      en: 'Chinese',
      es: 'Chino',
      fr: 'Chinois',
      ja: '中国語',
      ko: '중국어',
      ru: 'китайский',
      vi: 'Tiếng Trung',
      zh: '中文',
    },
    value: 'zh',
  },
  {
    enabled: true,
    flag: '/flags/FR.svg',
    name: {
      cn: '法文',
      de: 'Französisch',
      en: 'French',
      es: 'Francés',
      fr: 'Français',
      ja: 'フランス語',
      ko: '프랑스어',
      ru: 'французский',
      vi: 'Tiếng Pháp',
      zh: '法语',
    },
    value: 'fr',
  },
  {
    enabled: true,
    flag: '/flags/DE.svg',
    name: {
      cn: '德语',
      de: 'Deutsch',
      en: 'German',
      es: 'Alemán',
      fr: 'Allemand',
      ja: 'ドイツ語',
      ko: '독일어',
      ru: 'немецкий',
      vi: 'Tiếng Đức',
      zh: '德语',
    },
    value: 'de',
  },
  {
    enabled: true,
    flag: '/flags/RU.svg',
    name: {
      cn: '俄语',
      de: 'Russisch',
      en: 'Russian',
      es: 'Ruso',
      fr: 'Russe',
      ja: 'ロシア語',
      ko: '러시아어',
      ru: 'русский',
      vi: 'Tiếng Nga',
      zh: '俄语',
    },
    value: 'ru',
  },
  {
    enabled: true,
    flag: '/flags/ES.svg',
    name: {
      cn: '西班牙语',
      de: 'Spanisch',
      en: 'Spanish',
      es: 'Español',
      fr: 'Espagnol',
      ja: 'スペイン語',
      ko: '스페인어',
      ru: 'испанский',
      vi: 'Tiếng Tây Ban Nha',
      zh: '西班牙语',
    },
    value: 'es',
  },
] as const

export const dashboardWeekdays: DashboardWeekday[] = [
  {
    name: { cn: '星期一', de: 'Mo', en: 'Mon', es: 'lun', fr: 'Lun', ja: 'げつようび', ko: '월요일', ru: 'пон', vi: 'T2', zh: '星期一' },
    value: 'mon',
  },
  {
    name: { cn: '星期二', de: 'Di', en: 'Tue', es: 'mar', fr: 'Mar', ja: 'かようび', ko: '화요일', ru: 'вто', vi: 'T3', zh: '星期二' },
    value: 'tue',
  },
  {
    name: { cn: '星期三', de: 'Mi', en: 'Wed', es: 'mié', fr: 'Mer', ja: 'すいようび', ko: '수요일', ru: 'сре', vi: 'T4', zh: '星期三' },
    value: 'wed',
  },
  {
    name: { cn: '星期四', de: 'Do', en: 'Thu', es: 'jue', fr: 'Jeu', ja: 'もくようび', ko: '목요일', ru: 'чет', vi: 'T5', zh: '星期四' },
    value: 'thu',
  },
  {
    name: { cn: '星期五', de: 'Fr', en: 'Fri', es: 'vie', fr: 'Ven', ja: 'きんようび', ko: '금요일', ru: 'пят', vi: 'T6', zh: '星期五' },
    value: 'fri',
  },
  {
    name: { cn: '星期六', de: 'Sa', en: 'Sat', es: 'sáb', fr: 'Sam', ja: 'どようび', ko: '토요일', ru: 'суб', vi: 'T7', zh: '星期六' },
    value: 'sat',
  },
  {
    name: { cn: '星期日', de: 'So', en: 'Sun', es: 'dom', fr: 'Dim', ja: 'にちようび', ko: '일요일', ru: 'вос', vi: 'CN', zh: '星期日' },
    value: 'sun',
  },
] as const

export const initialDashboardState: DashboardState = {
  book: null,
  books: [],
  enterKeyPremium: false,
  errorMessage: null,
  inWeek: [...dashboardWeekdays],
  isBootstrapping: true,
  isLessonsLoading: false,
  lesson: null,
  lessons: [],
  lessonsBookId: null,
  lessonsErrorMessage: null,
  listeningQuizSession: null,
  languages: [...dashboardLanguages],
  myUser: null,
  screen: 0,
  settingsTab: null,
  selectPremiumAccountPayment: false,
  showChangeDailyGoal: false,
  showLanguage: false,
}

export const DashboardStateContext =
  createContext<DashboardStateContextValue | null>(null)
